import { CheckCircle2, XCircle, Phone, Users, Trash2, Printer } from "lucide-react";
import { TableCell, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { generateQRCodeDataURL } from "@/utils/qrCodeGenerator";
import { createPDFPage, generatePDF } from "@/utils/pdfGenerator";

type QRCodeRowProps = {
  token: string;
  created_at: string;
  assigned_group: 'intervention' | 'control' | null;
  responses: {
    response_number: number;
    created_at: string | null;
    phone_number: string | null;
  }[];
};

export const QRCodeRow = ({ token, created_at, assigned_group, responses }: QRCodeRowProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const latestResponse = responses.sort((a, b) => 
    (b.created_at || '').localeCompare(a.created_at || '')
  )[0];
  
  // Get the first response (which contains the phone number)
  const firstResponse = responses.find(r => r.response_number === 1);

  // Check if there's at least one response (meaning assignment has happened)
  const hasResponses = responses.length > 0;
  
  // Calculate the maximum response number achieved by the participant
  const maxResponseNumber = responses.length > 0
    ? Math.max(0, ...responses.map(r => r.response_number || 0))
    : 0;
    
  // Determine completion status for each "Svar" (Visit/Time)
  // Each visit consists of 3 survey parts, so:
  // Visit 1 (Time 1) = response_numbers 1-3
  // Visit 2 (Time 2) = response_numbers 4-6
  // Visit 3 (Time 3) = response_numbers 7-9
  const svar1Completed = maxResponseNumber >= 3; // Visit 1 completed all 3 parts
  const svar2Completed = maxResponseNumber >= 6; // Visit 2 completed all 3 parts
  const svar3Completed = maxResponseNumber >= 9; // Visit 3 completed all 3 parts

  const handleDelete = async () => {
    try {
      // Get current user
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        throw new Error("No authenticated user found");
      }

      // Delete the token
      const { error } = await supabase
        .from('auth_tokens')
        .delete()
        .eq('token', token);

      if (error) throw error;

      await queryClient.invalidateQueries({ queryKey: ['qrCodes'] });
      
      toast({
        title: "Framgång",
        description: "QR-koden har tagits bort",
      });
    } catch (error) {
      console.error('Error deleting QR code:', error);
      toast({
        title: "Fel",
        description: "Det gick inte att ta bort QR-koden",
        variant: "destructive",
      });
    }
  };

  const handleReprint = async () => {
    try {
      const container = document.createElement("div");
      const qrCodeDataUrl = await generateQRCodeDataURL(token);
      const page = await createPDFPage(token, qrCodeDataUrl);
      container.appendChild(page);
      document.body.appendChild(container);
      await generatePDF(container);
      document.body.removeChild(container);

      toast({
        title: "Framgång",
        description: "QR-koden har skrivits ut igen",
      });
    } catch (error) {
      console.error('Error reprinting QR code:', error);
      toast({
        title: "Fel",
        description: "Det gick inte att skriva ut QR-koden igen",
        variant: "destructive",
      });
    }
  };

  return (
    <TableRow>
      <TableCell className="font-mono text-xs w-20 min-w-20 break-all">{token}</TableCell>
      <TableCell className="w-20 min-w-20 text-sm">{new Date(created_at).toLocaleDateString('sv-SE')}</TableCell>
      <TableCell className="w-28 min-w-28">
        {firstResponse?.phone_number ? (
          <div className="flex items-center gap-1">
            <Phone className="h-3 w-3 flex-shrink-0" />
            <span className="text-sm truncate">{firstResponse.phone_number}</span>
          </div>
        ) : '-'}
      </TableCell>
      <TableCell className="w-20 min-w-20">
        {hasResponses && assigned_group ? (
          <div className="flex items-center gap-1">
            <Users className="h-3 w-3 flex-shrink-0" />
            <span className="text-xs truncate">
              {assigned_group === 'intervention' ? 'Interv.' : 'Kontroll'}
            </span>
          </div>
        ) : (
          <span className="text-muted-foreground text-xs">Inte tilldelad</span>
        )}
      </TableCell>
      <TableCell className="w-12 min-w-12 text-center">
        {svar1Completed ? (
          <CheckCircle2 className="h-4 w-4 text-green-500 mx-auto" />
        ) : (
          <XCircle className="h-4 w-4 text-gray-300 mx-auto" />
        )}
      </TableCell>
      <TableCell className="w-12 min-w-12 text-center">
        {svar2Completed ? (
          <CheckCircle2 className="h-4 w-4 text-green-500 mx-auto" />
        ) : (
          <XCircle className="h-4 w-4 text-gray-300 mx-auto" />
        )}
      </TableCell>
      <TableCell className="w-12 min-w-12 text-center">
        {svar3Completed ? (
          <CheckCircle2 className="h-4 w-4 text-green-500 mx-auto" />
        ) : (
          <XCircle className="h-4 w-4 text-gray-300 mx-auto" />
        )}
      </TableCell>
      <TableCell className="w-20 min-w-20">
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            onClick={handleReprint}
            title="Skriv ut QR-kod igen"
            className="h-8 w-8 p-0"
          >
            <Printer className="h-3 w-3" />
          </Button>
          {responses.length === 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleDelete}
              className="text-destructive hover:text-destructive h-8 w-8 p-0"
              title="Ta bort QR-kod"
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
};
